import { createTheme, Theme } from '@mui/material/styles';
import createCache from '@emotion/cache';
import type { EmotionCache } from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';

/**
 * Create MUI theme with RTL/LTR support
 * @param direction - 'rtl' or 'ltr'
 * @returns MUI Theme object
 */
export const createDirectionalTheme = (direction: 'rtl' | 'ltr'): Theme => {
  return createTheme({
    direction,
    typography: {
      fontFamily: direction === 'rtl' 
        ? 'Arial, Tahoma, sans-serif' // RTL-friendly fonts
        : 'HerbalifeFont', // Your existing font
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            direction,
          },
        },
      },
      // Add direction-aware component overrides
      MuiButton: {
        styleOverrides: {
          root: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              textAlign: direction === 'rtl' ? 'right' : 'left',
            },
          },
        },
      },
    },
  });
};

/**
 * Create Emotion cache for RTL
 * This cache includes the rtlPlugin to transform CSS properties
 * @returns EmotionCache for RTL
 */
export const createRtlCache = (): EmotionCache => {
  return createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
  });
};

/**
 * Create Emotion cache for LTR (default)
 * @returns EmotionCache for LTR
 */
export const createLtrCache = (): EmotionCache => {
  return createCache({
    key: 'muiltr',
  });
};

/**
 * Get appropriate cache based on direction
 * @param direction - 'rtl' or 'ltr'
 * @returns EmotionCache
 */
export const getCacheForDirection = (direction: 'rtl' | 'ltr'): EmotionCache => {
  return direction === 'rtl' ? createRtlCache() : createLtrCache();
};

